import { formatDate, formatPrice } from 'helpers';
import { formDataTableCell, houseNameWithChange, pageBreak } from 'hooks/usePdfMaker/documentsCommon';
import { ICalculation, IVersion } from 'models/calculationModel';
import { ICase } from 'models/models';
import { IPriceResponse } from 'models/priceModel';
import { documentCheckBox, makeCustomersRows, paymentPlanUnderline } from './helpers';

type Args = {
  version: IVersion;
  currentCase: ICase;
  price: IPriceResponse;
  calculation: ICalculation;
  gdpr?: boolean;
};
export const getContratB2BDocument =
  (_gdpr: boolean) =>
  ({ version, currentCase, gdpr, calculation, price }: Args) => {
    if (!calculation) return {};
    const versionNumber = `${currentCase.caseNumber}-${version.version}`;
    const firstCustomer = currentCase.customers[0];
    const hasGarage = !calculation.currentHouse.house.parameters.garageType.productData.rules.isNoneProduct;
    const houseName = houseNameWithChange(calculation);

    const doc = {
      info: {
        title: 'Avtal',
      },
      footer: (currentPage: any, pageCount: any) => {
        return {
          margin: [40, 0, 0, 0],
          text: `Avtal ABM07 (${versionNumber}), sida ${currentPage.toString()} av ${pageCount}`,
        };
      },
      content: [
        {
          table: {
            widths: [30, '*', 140, 40, 'auto'],
            layout: 'noBorders',
            body: [
              [
                {
                  image: 'logo',
                  width: 30,
                },
                {
                  text: [{ text: 'Köpeavtal', style: 'header' }],
                  colSpan: 3,
                },
                {},
                {},
                {
                  qr: 'this.data.versionData',
                  fit: 60,
                },
              ],
            ],
          },
        },
        {
          table: {
            widths: ['*', '*', '*', '*', '*', '*', '*'],
            body: [
              [
                formDataTableCell('Säljare', 'Bra Hus från Hedlunds AB', 4),
                {},
                {},
                {},
                formDataTableCell('Org nr.', '556609-1996', 2),
                {},
                formDataTableCell('Avtalsnr.', ''),
              ],
              ...makeCustomersRows(currentCase.customers, gdpr),
              [
                formDataTableCell('Bostadsadress', gdpr ? '[...]' : firstCustomer.address, 3),
                {},
                {},
                formDataTableCell('Postadress', gdpr ? '[...]' : firstCustomer.postalAddress, 3),
                {},
                {},
                formDataTableCell('Tel. bostad', gdpr ? '[...]' : firstCustomer.phoneHome),
              ],
              [
                formDataTableCell('Köparens e-mailadress', gdpr ? '[...]' : firstCustomer.email, 3),
                {},
                {},
                formDataTableCell('Säljarens representant/säljkontor', currentCase.userName, 3),
                {},
                {},
                formDataTableCell('Tel.', ''),
              ],
              [
                formDataTableCell('Fastighetsbeteckning', currentCase.propertyDesignation || ' ', 2),
                {},
                formDataTableCell('Kommun', currentCase.propertyMunicipal || ' ', 2),
                {},
                formDataTableCell('Byggplatsadress', currentCase.propertyAddress || ' ', 3),
                {},
                {},
              ],
              [
                formDataTableCell('Hustyp', houseName, 3),
                {},
                {},
                formDataTableCell('Ritningsnr.', currentCase.drawingNumberHouse || ''),
                formDataTableCell('Sidobyggnad', hasGarage ? 'Ja' : 'Nej'),
                formDataTableCell('Ritningsnr.', currentCase.drawingNumberGarage || ''),
                formDataTableCell('Rätvänd', currentCase.isInverted ? 'Nej' : 'Ja'),
              ],
            ],
          },
        },
        {
          text: [
            'Säljaren åtager sig att på ovanstående fastighet leverera material enligt detta avtal och nedan angivna handlingar\n',
            'och specifikationer som köparen tagit del av. Avtalet innefattar endast materialleverans, byggplatsarbeten ingår ej.',
          ],
          alignment: 'center',
          margin: [0, 5, 5, 0],
        },
        {
          margin: [0, 10, 0, 0],
          table: {
            widths: ['*', 'auto'],
            body: [
              [{ text: 'Specifikation och avtalssumma' }, { text: 'Pris kronor' }],
              [
                { text: 'Materialleverans' },
                {
                  text: formatPrice(price.currentPriceForHouseAndGarage),
                },
              ],
              [{ text: ' ' }, { text: ' ' }],
              [{ text: 'Avtalssumma' }, { text: formatPrice(price.currentPriceForHouseAndGarage) }],
              [
                {
                  border: [true, true, true, false],
                  text: 'Varav mervärdesskatt 25%',
                },
                {
                  border: [true, true, true, false],
                  text: formatPrice(Math.round(price.currentPriceForHouseAndGarage * 0.2)),
                },
              ],
              [
                {
                  margin: [0, 0, 0, 0],
                  border: [true, false, true, true],
                  table: {
                    widths: ['*', 'auto'],
                    body: [
                      [
                        {
                          border: [false, false, false, false],
                          text: `Indexreglering sker enligt MT 74 (se levereransdeklaration). Basmånad är: ${currentCase.indexBaseMonth}`,
                          style: 'tableMicroText',
                        },
                        {
                          border: [false, false, false, false],
                          text: `år: ${currentCase.indexBaseYear}`,
                          style: 'tableMicroText',
                        },
                      ],
                    ],
                  },
                },
                {
                  border: [true, false, true, true],
                  text: '',
                },
              ],
            ],
          },
        },
        {
          margin: [0, 20, 0, 0],
          text: 'Villkor och handlingar',
          style: { bold: true },
        },
        {
          table: {
            // widths: [300, 200],
            widths: ['*'],
            body: [
              [
                {
                  layout: 'noBorders',
                  table: {
                    width: [20, '*', 'auto'],
                    body: [
                      [{ text: 'Handlingar', colSpan: 3 }, '', ''],
                      [documentCheckBox(), { text: 'Detta kontrakt' }, ''],
                      [documentCheckBox(), { text: 'ABM07, garantitid 2 år' }, ''],
                      [
                        documentCheckBox(),
                        {
                          text: [
                            'Offert nr. ',
                            {
                              text: versionNumber,
                              style: { bold: true },
                            },
                          ],
                        },
                        {
                          text: [
                            'Daterad: ',
                            {
                              text: formatDate(version.dateSaved, 'yyyy-MM-dd'),
                              style: { bold: true },
                            },
                          ],
                        },
                        // { text: 'Daterad: 2018-08-21' }
                      ],
                      [
                        documentCheckBox(),
                        {
                          text: [
                            'Leveransdeklaration: ',
                            {
                              text: versionNumber,
                              style: { bold: true },
                            },
                          ],
                        },
                        {
                          text: [
                            'Daterad: ',
                            {
                              text: formatDate(version.dateSaved, 'yyyy-MM-dd'),
                              style: { bold: true },
                            },
                          ],
                        },
                      ],
                      [
                        documentCheckBox(),
                        {
                          text: `Tillhörande daterad avtalsritning/skiss ${
                            currentCase.drawingHouseCreatedDate &&
                            formatDate(currentCase.drawingHouseCreatedDate, 'yyyy-MM-dd')
                          }`,
                        },
                        {
                          text: `senast reviderad: ${
                            currentCase.drawingHouseChangedDate &&
                            formatDate(currentCase.drawingHouseChangedDate, 'yyyy-MM-dd')
                          }`,
                        },
                      ],
                      [
                        {
                          colSpan: 3,
                          margin: [0, 5, 0, 0],
                          text: [
                            {
                              text: 'Leveranstid fastställs då slutlig tillverkningsorder inlämnats till Bra Hus. I samband därmed skall Köparen också överlämna säkerhet, godkänd ritning, bevis om bygglov och andra tillstånd.\n',
                            },
                          ],
                        },
                        { text: '' },
                        { text: '' },
                      ],
                      [
                        {
                          colSpan: 3,
                          margin: [0, 5, 0, 0],
                          text: [
                            {
                              text: 'I och med detta avtal, upphör tidigare avtal att gälla. Muntliga avtal bör snarast bekräftas skriftligen.\n',
                            },
                            // {
                            //   text: 'I de fall Köparen ej beviljas bygglov eller byggnadslån, kan detta avtal annulleras mot en fast ersättning till Säljaren motsvarande erlagd handpenning. Vid avbeställning i annat fall har Bra Hus alltid rätt att behålla lägst erlagd handpenning samt eventuell ersättning för ytterligare överskjutande kostnader i enlighet med AA12.',
                            // },
                          ],
                        },
                        { text: '' },
                        { text: '' },
                      ],
                    ],
                  },
                },
              ],
            ],
          },
        },
        pageBreak(),
        {
          margin: [0, 20, 0, 0],
          text: 'Betalning och säkerhet',
          style: { bold: true },
        },
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  layout: 'noBorders',
                  table: {
                    widths: ['*', '*'],
                    body: [
                      [{ text: 'Betalningsplan enligt särskilld överenskommelse', colSpan: 2 }, ''],
                      [
                        { text: 'Bilaga daterad' },
                        [{ text: ' ', alignment: 'right' }, paymentPlanUnderline()],
                      ],
                      // [
                      //   { text: 'Från första materialleverans' },
                      //   [{ text: '10 dagar 75%', alignment: 'right' }, paymentPlanUnderline()],
                      // ],
                      // [
                      //   { text: 'Stomkomplettering' },
                      //   [{ text: '60 dagar 20%', alignment: 'right' }, paymentPlanUnderline()],
                      // ],
                      // [
                      //   { text: 'Vid slutbesiktning (*)' },
                      //   [{ text: '5%', alignment: 'right' }, paymentPlanUnderline()],
                      // ],

                      // [
                      //   { text: 'Summa', style: { bold: true } },
                      //   { text: '100%', alignment: 'right', style: { bold: true } },
                      // ],
                      // [
                      //   {
                      //     colSpan: 2,
                      //     margin: [0, 3, 0, 0],
                      //     text: '(*) Besiktningsmannen värderar i samband med slutbesiktningen hur stort belopp som skall kvarstå tills eventuella fel är åtgärdade. Besiktning skall ske inom 6 månader från första materialleverans. Sker ej besiktning inom föreskriven tid indexregleras eventuell kvarstående skuld.',
                      //   },
                      //   { text: '' },
                      // ],
                      [
                        {
                          colSpan: 2,
                          margin: [0, 5, 0, 0],
                          text: [
                            { text: 'Säkerhet till Säljaren\n', style: { bold: true } },
                            { text: 'Bankgaranti eller annan handling som säljaren godtar.' },
                            // { text: 'Säkerhet till Köparen\n', style: { bold: true } },
                            // {
                            //   text: 'Bra Hus tecknar via Gar-Bo avtal om en färdigställandeförsäkring på Bra Hus materialleverans. Avtalet omfattar även slutbesiktning av Gar-Bo utsedd besiktningsman.\n\n',
                            // },
                            // { text: 'Säkerhet till Säljaren\n', style: { bold: true } },
                            // {
                            //   text: 'Bankgaranti eller spärr i byggnadslån avseende hela avtalssumman inklusive slutliga ändringar.\n',
                            // },
                          ],
                        },
                        { text: '' },
                      ],
                    ],
                  },
                },
              ],
            ],
          },
        },
        [
          {
            margin: [0, 10, 0, 0],
            text: 'Eventuella ändringar och tillval regleras separat.',
          },
          'Avtalet upphör om köparen inte inkommer med slutlig besiktningsorder inom 24 månader från avtalets undertecknande.',
          'Säljaren har i sådant fall, samt när köparen i annat fall annulerar avtalet före inlämnandet av slutlig tillverkningsorder, rätt att erhålla ersättning för havda kostnader samt eventuell handpenning.',
        ],

        [
          {
            margin: [0, 10, 0, 0],
            text: 'Detta avtal har upprättats i två exemplar, varav parterna tagit varsitt. Köparen får sitt exemplar när köpet godkänts av Säljaren. Avtal ingås först när Säljaren undertecknat och godkänt avtalet.',
            style: { italic: true },
          },
        ],
        {
          margin: [0, 20, 0, 0],
          layout: 'noBorders',
          table: {
            widths: [10, '*', 'auto'],
            body: [
              [
                documentCheckBox(false),
                {
                  text: 'Köparen godkänner att Bra Hus använder fotografier/film från Köparens husbygge i kommersiellt syfte.',
                  margin: [0, 2, 0, 0],
                },
                '',
              ],
            ],
          },
        },
        // signingLines(currentCase.customers),
        [
          {
            margin: [0, 30, 0, 0],
            columns: [
              {
                width: 260,
                text: [
                  { text: '\n' },
                  { text: 'Ort och datum för Köparens underskrift\n' },
                  { text: ' \n\n\n', style: { fontSize: 14, bold: true } },
                  { text: 'Köparens underskrift' },
                ],
              },
              {
                width: 260,
                text: [
                  { text: '\n' },
                  { text: 'Ort och datum för Säljarens godkännande\n' },
                  { text: '\n\n\n\n\n' },
                  // {text: 'Bra Hus från Hedlunds AB\n\n\n', style: {fontSize: 14, bold: true}},
                  { text: 'Behörig företrädare för Säljaren' },
                ],
              },
            ],
          },
          {
            // Streck och linjer
            canvas: [
              // Ort & datum, köpare
              {
                type: 'line',
                x1: 0,
                y1: -70,
                x2: 230,
                y2: -70,
                dash: { length: 2 },
                lineWidth: 1,
              },

              // Ort & datum, Bra Hus
              {
                type: 'line',
                x1: 260,
                y1: -70,
                x2: 480,
                y2: -70,
                dash: { length: 2 },
                lineWidth: 1,
              },

              // Underskrift säljare
              {
                type: 'line',
                x1: 0,
                y1: -15,
                x2: 230,
                y2: -15,
                dash: { length: 2 },
                lineWidth: 1,
              },

              // Underskrift Bra Hus
              {
                type: 'line',
                x1: 260,
                y1: -15,
                x2: 480,
                y2: -15,
                dash: { length: 2 },
                lineWidth: 1,
              },
            ],
          },
        ],
        {
          margin: [0, 20, 0, 0],
          italics: true,
          text: 'Bra Hus samlar in och hanterar dina personuppgifter i syfte att fullfölja förbindelserna i detta avtal eller enligt lag samt för att fullfölja kundrelationen. Vi hanterar dina personuppgifter endast för avsett ändamål och aldrig längre än nödvändigt. För mer information om vår integritetspolicy gå in på www.brahus.se',
        },
      ],

      styles: {
        header: {
          fontSize: 18,
          bold: true,
        },
        bold: {
          bold: true,
        },
        boldList: {
          bold: true,
          margin: [5, 5, 0, 0],
        },
        microtext: {
          italics: true,
          fontSize: 6,
        },
        tableHeadRow: {
          fillColor: '#b9c4a5',
          border: [false, false, false, false],
        },
        infoTable: {
          margin: [0, 20, 0, 0],
        },
        sectionHeadline: {
          fontSize: 18,
          bold: true,
          fillColor: '#DDDDDD',
          border: [false, false, false, false],
        },
        tableHeader: {
          bold: true,
        },
        tableMicroText: {
          fontSize: 6,
        },

        hr: {
          margin: [0, 5, 0, 0],
        },
        date: {
          margin: [0, 60, 0, 20],
        },
      },
      defaultStyle: {
        fontSize: 8,
      },
      images: {
        logo: 'data:image/png;base64,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',
      },
    };
    return doc;
  };
